import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

import { currentUserQuery, selectActiveMembership, selectImpersonation, type PartnerCockpitUser } from './auth';
import { forgetOtherStoredUser, useMixpanel } from './mixpanelContext';
import { hasCockpitSessionHint } from './sessionHint';

// Ops impersonating a partner would otherwise count as that partner's activity.
export function isMixpanelSuppressedFor(user: PartnerCockpitUser | undefined): boolean {
    return selectImpersonation(user) !== undefined;
}

// Ids and what the KPIs segment by, nothing personal, matching the consumer platform. `$name` is the uuid so the
// Mixpanel profile list shows something without carrying the real name.
export function selectMixpanelUserProperties(user: PartnerCockpitUser): Record<string, unknown> {
    const membership = selectActiveMembership(user);
    return {
        $name: user.uuid,
        userUuid: user.uuid,
        partnerUuid: membership?.partnerUuid,
        partnerRole: membership?.role,
        // Splits subscribers from partners still on an old credits contract, which several KPIs report separately.
        usesCredits: membership?.usesCredits,
    };
}

// Follows the cached user without fetching it: routes already load it, and every teardown path (logout, an
// expired session, stopping an impersonation) ends by clearing it from the cache. Read from the cache rather than
// through `useQuery`, whose observer keeps serving the last data after its query is removed.
export function useMixpanelIdentity(): void {
    const queryClient = useQueryClient();
    const user = useSyncExternalStore(
        useCallback((onChange: () => void) => queryClient.getQueryCache().subscribe(onChange), [queryClient]),
        () => queryClient.getQueryData(currentUserQuery.queryKey),
    );
    const mixpanel = useMixpanel();
    const identifiedUuid = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (!user || isMixpanelSuppressedFor(user)) {
            if (identifiedUuid.current) {
                mixpanel.reset();
                identifiedUuid.current = undefined;
            } else if (!user && mixpanel.storedUserId() !== undefined && !hasCockpitSessionHint()) {
                // The SDK keeps the last identity in localStorage, so a session that ended while the tab was closed
                // would otherwise send this visit's anonymous pages as the previous user and partner. Only a stored
                // user is cleared: an anonymous visitor keeps their device, so the register page and the start link
                // opened in another tab still join up once they are identified.
                mixpanel.reset();
            }
            return;
        }
        if (identifiedUuid.current !== user.uuid) {
            forgetOtherStoredUser(mixpanel, user.uuid);
            mixpanel.identify(user.uuid);
            identifiedUuid.current = user.uuid;
        }
        mixpanel.setUserProperties(selectMixpanelUserProperties(user));
        // On every event rather than only the profile, so each event names the partner it happened for at the time.
        mixpanel.setEventProperties({ partnerUuid: selectActiveMembership(user)?.partnerUuid });
    }, [user, mixpanel]);
}

export function MixpanelIdentity(): null {
    useMixpanelIdentity();
    return null;
}
